import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, RefreshCw } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ProductCard from '@/components/products/ProductCard';
import axios from 'axios';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

type Product = {
  id: string;
  title: string;
  description: string | null;
  image: string | null;
  rating: number;
  tags?: string[];
  group_id: string | null;
};

type Group = {
  id: string;
  name: string;
  description: string | null;
  sort_order?: number;
};

const Products: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    axios.defaults.timeout = 30000;
    axios.defaults.headers.common['Cache-Control'] = 'no-cache';
    axios.defaults.headers.common['Accept'] = 'application/json';
  }, []);

  useEffect(() => {
    fetchData();
  }, [retryCount]);

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await fetchGroups();
      await fetchProducts();
    } catch (err: any) {
      const msg = err instanceof Error ? err.message : '请求失败';
      setError(msg);
      toast({ title: 'Error', description: msg, variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const res = await axios.get('/api/products/groups', {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        timeout: 10000,
      });
      const data = Array.isArray(res.data) ? res.data : [];
      const sorted = data.sort(
        (a: Group, b: Group) => (a.sort_order || 0) - (b.sort_order || 0)
      );
      setGroups(sorted);
    } catch (err) {
      console.error('Error fetching groups:', err);
      setGroups([]);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get('/api/products', {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
        timeout: 15000,
      });
      const data: Product[] = Array.isArray(res.data) ? res.data : [];
      const withTags = await Promise.all(
        data.map(async (product) => {
          if (Array.isArray(product.tags)) return product;
          try {
            const tagRes = await axios.get(
              `/api/products/${product.id}/tags`,
              { headers: { Accept: 'application/json' } }
            );
            const tags = Array.isArray(tagRes.data)
              ? tagRes.data.map((t: any) => t.name)
              : [];
            return { ...product, tags };
          } catch {
            return { ...product, tags: [] };
          }
        })
      );
      setProducts(withTags);
    } catch (err) {
      console.error('Error fetching products:', err);
      setProducts([]);
    }
  };

  const handleRetry = () => {
    setRetryCount((prev) => prev + 1);
    toast({ title: 'Retrying', description: '正在重新加载…' });
  };

  const filteredProducts = products.filter((p) => {
    const text = searchTerm.toLowerCase();
    const matchSearch =
      p.title.toLowerCase().includes(text) ||
      (p.description?.toLowerCase().includes(text) ?? false);
    const matchGroup = selectedGroup ? p.group_id === selectedGroup : true;
    return matchSearch && matchGroup;
  });

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        <section className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-gray-900 dark:to-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h1 className="text-4xl font-bold text-gray-900 dark:text-white">
              Products
            </h1>
            <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Explore the best products to help you ship your next application
              faster and better
            </p>
            {error && (
              <Alert variant="destructive" className="mt-6 mx-auto max-w-lg">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Error</AlertTitle>
                <AlertDescription className="flex items-center justify-between">
                  <span>{error}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleRetry}
                  >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            <div className="max-w-md mx-auto mt-6 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-md py-2 pl-10 pr-4 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </section>
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-6">
              {/* Left sidebar with groups */}
              <div className="w-full md:w-64 flex-shrink-0">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                  <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-white">
                    Product Groups
                  </h3>
                  <div className="space-y-2">
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, index) => (
                        <div
                          key={`loading-group-${index}`}
                          className="h-8 bg-gray-200 dark:bg-gray-700 animate-pulse rounded"
                        ></div>
                      ))
                    ) : groups.length > 0 ? (
                      <>
                        {/* Add "All Products" option */}
                        <button
                          onClick={() => setSelectedGroup(null)}
                          className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                            selectedGroup === null
                              ? 'bg-primary text-white'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          All Products
                        </button>
                        {/* List all groups */}
                        {groups.map((group) => (
                          <button
                            key={group.id}
                            onClick={() => setSelectedGroup(group.id)}
                            className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                              selectedGroup === group.id
                                ? 'bg-primary text-white'
                                : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                            }`}
                          >
                            {group.name}
                          </button>
                        ))}
                      </>
                    ) : (
                      <p className="text-gray-500">No groups found</p>
                    )}
                  </div>
                </div>
              </div>
              {/* Right side with products */}
              <div className="flex-1">
                <div className="mb-8">
                  <h2 className="text-2xl font-bold">
                    {selectedGroup && groups.length > 0
                      ? groups.find((g) => g.id === selectedGroup)?.name ||
                        'Products'
                      : 'All Products'}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {isLoading ? (
                    Array.from({ length: 6 }).map((_, index) => (
                      <div
                        key={`loading-product-${index}`}
                        className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden animate-pulse"
                      >
                        <div className="h-48 bg-gray-200 dark:bg-gray-700"></div>
                        <div className="p-4 space-y-3">
                          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
                        </div>
                      </div>
                    ))
                  ) : filteredProducts.length > 0 ? (
                    filteredProducts.map((product) => (
                      <ProductCard key={product.id} product={product} />
                    ))
                  ) : (
                    <div className="col-span-full text-center py-12">
                      <p className="text-gray-500 text-lg">
                        {searchTerm
                          ? `No products found matching "${searchTerm}".`
                          : 'No products found in this group.'}
                      </p>
                      <Button
                        variant="outline"
                        className="mt-4"
                        onClick={handleRetry}
                      >
                        Refresh Products
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Products;
